import { useState } from 'react';
import { FormsV1 } from '@frontend/api-digital-forms';
import { http } from '@frontend/fetch';
import { DEFAULT_DFX_HTTP_OPTIONS } from '../../constants';
import { getISODate } from '../../helpers/date-helpers';

type SearchPatientParams = Pick<
  FormsV1.Types.SearchPatientForKioskV2IO['input'],
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phoneNumber'
  | 'birthdate'
  | 'companyId'
  | 'city'
  | 'state'
  | 'streetAddress1'
  | 'streetAddress2'
  | 'zipCode'
>;

type UseSearchPatientResult = {
  isSearching: boolean;
  searchPatient: (searchParams: SearchPatientParams) => Promise<{
    success: boolean;
    patientDetails?: FormsV1.Types.SearchPatientForKioskV2IO['output']['personDetails'];
    hasMultipleResults?: boolean;
    isSyncAppError?: boolean;
  }>;
};

export const useSearchPatient = (): UseSearchPatientResult => {
  const [isLoading, setIsLoading] = useState(false);

  const searchPatientForKiosk = FormsV1.Queries.useFetchSearchPatientForKioskV2();

  const searchPatient: UseSearchPatientResult['searchPatient'] = async (searchParams) => {
    const request: FormsV1.Types.SearchPatientForKioskV2IO['input'] = {
      ...searchParams,
      birthdate: getISODate(searchParams.birthdate),
    };
    try {
      setIsLoading(true);
      const res = await searchPatientForKiosk({
        request,
        options: { staleTime: 0 },
        httpOptions: DEFAULT_DFX_HTTP_OPTIONS,
      });

      return { success: true, patientDetails: res.personDetails };
    } catch (error) {
      let hasMultipleResults = false;
      let isSyncAppError = false;

      // Extract specific error messages if available
      if (http.isHttpError(error) && error.data) {
        if (typeof error.data === 'object' && 'message' in error.data) {
          const message = error.data.message;

          if (typeof message === 'string') {
            if (message.includes('MULTIPLE_ENTITIES_FOUND')) {
              hasMultipleResults = true;
            } else if (message.includes('DISCONNECTED_SYNC_APP') || message.includes('sync app disconnected')) {
              isSyncAppError = true;
            }
          }
        }
      }

      return { success: false, hasMultipleResults, isSyncAppError };
    } finally {
      setIsLoading(false);
    }
  };

  return { isSearching: isLoading, searchPatient };
};
